import {StyleSheet, View} from 'react-native';
import {
  ActivityIndicator,
  DataTable,
  Paragraph,
  Subheading,
  Surface,
  Text,
} from 'react-native-paper';

import {
  CreditTransaction,
  CreditType,
  useCreditTypes,
  useFetchLimitedPartners,
} from '../hooks/useDomainData';
import {useAppSettings} from './AppSettings';

export function getCreditBalancesByLPIdByFundByType(
  txns?: ReadonlyArray<CreditTransaction>,
): {[key: string]: {[key: string]: {[key: string]: number}}} {
  // Map of LPs containing map of Funds containing map of credit types with totals
  const creditsByLPByFundByType: {
    [key: string]: {[key: string]: {[key: string]: number}};
  } = {};

  txns?.map(txn => {
    const fund = txn.creditType?.fund?.fund;
    if (fund === undefined) {
      return;
    }
    const creditType = txn.creditType?.creditType;
    if (creditType === undefined) {
      return;
    }
    const lp = txn.limitedPartner?.ref.id;
    if (lp === undefined) {
      return;
    }

    // if the credit type has expired, we just ignore it
    if (
      txn.creditType?.expirationYear &&
      txn.creditType.expirationYear.toDate() < new Date()
    ) {
      // console.log('CreditTransaction is for type that has expired. Skipping.');
      return;
    }

    if (creditsByLPByFundByType[lp] === undefined) {
      creditsByLPByFundByType[lp] = {};
    }
    if (creditsByLPByFundByType[lp][fund] === undefined) {
      creditsByLPByFundByType[lp][fund] = {};
    }
    if (creditsByLPByFundByType[lp][fund][creditType] === undefined) {
      creditsByLPByFundByType[lp][fund][creditType] = 0;
    }
    creditsByLPByFundByType[lp][fund][creditType] += Number.parseInt(
      txn.credits,
      10,
    );

    // If we have zeroed out a credit type, purge it
    if (creditsByLPByFundByType[lp][fund][creditType] === 0) {
      delete creditsByLPByFundByType[lp][fund][creditType];
    }

    // If we have zeroed out all credit types for a fund, purge it
    if (Object.keys(creditsByLPByFundByType[lp][fund]).length === 0) {
      delete creditsByLPByFundByType[lp][fund];
    }
  });

  return creditsByLPByFundByType;
}

export function CreditBalances(props: {
  txns?: ReadonlyArray<CreditTransaction>;
}): JSX.Element {
  const txns = props.txns;

  const appSettings = useAppSettings();
  const limitedPartners = useFetchLimitedPartners();
  const creditTypes = useCreditTypes();

  const themedStyles = StyleSheet.create({
    limitedPartnerContainer: {
      marginTop: 5,
      paddingTop: 10,
      borderTopWidth: 1,
      borderColor: appSettings.currentTheme.colors.primary,
    },
  });

  // Map of LPs containing map of Funds containing map of credit types with totals
  const creditsByLPIdByFundByType = getCreditBalancesByLPIdByFundByType(txns);

  if (!txns) {
    return (
      <Surface style={styles.focusedSurface}>
        <View>
          <ActivityIndicator size={'small'} />
        </View>
      </Surface>
    );
  }

  if (txns && txns.length === 0) {
    return (
      <Surface style={styles.focusedSurface}>
        <View>
          <Text>No credit transactions found, zero balance.</Text>
        </View>
      </Surface>
    );
  }

  return (
    <Surface style={styles.focusedSurface}>
      <Subheading>Credit Balances</Subheading>
      {!txns && <ActivityIndicator size={'small'} />}
      {txns && Object.keys(creditsByLPIdByFundByType).length <= 0 && (
        <Paragraph style={styles.center}>
          No transactions found, zero credit balance
        </Paragraph>
      )}
      {txns &&
        Object.keys(creditsByLPIdByFundByType).map(lpid => {
          const lpCreditsByFund = creditsByLPIdByFundByType[lpid];
          return (
            <View style={themedStyles.limitedPartnerContainer} key={lpid}>
              <Text>
                Balance for{' '}
                {
                  limitedPartners?.find(lp => lp.ref.id === lpid)
                    ?.limitedPartnerName
                }
              </Text>
              {Object.keys(lpCreditsByFund).length <= 0 && (
                <Paragraph style={styles.center}>Zero credit balance</Paragraph>
              )}

              {txns &&
                creditTypes &&
                Object.keys(lpCreditsByFund).length > 0 && (
                  <FundsCreditTable
                    lpCreditsByFund={lpCreditsByFund}
                    creditTypes={creditTypes}
                  />
                )}
            </View>
          );
        })}
    </Surface>
  );
}

function FundsCreditTable(props: {
  lpCreditsByFund: {
    [key: string]: {[key: string]: number};
  };
  creditTypes: CreditType[];
}): JSX.Element {
  const getCreditTypeExpiration = (creditTypeName: string): string => {
    const creditType = props.creditTypes.find(type => {
      if (type.creditType === creditTypeName) {
        return true;
      }
    });

    return creditType?.expirationYear?.toDate().toLocaleDateString() ?? '';
  };

  return (
    <View style={styles.content}>
      {Object.keys(props.lpCreditsByFund).map(fund => {
        let fundTotal = 0;
        return (
          <DataTable key={fund}>
            <DataTable.Header>
              <DataTable.Title>Fund</DataTable.Title>
              <DataTable.Title>Credit Type</DataTable.Title>
              <DataTable.Title>Expires</DataTable.Title>
              <DataTable.Title numeric>Credits</DataTable.Title>
            </DataTable.Header>
            {Object.keys(props.lpCreditsByFund[fund]).map(creditType => {
              fundTotal += props.lpCreditsByFund[fund][creditType];
              return (
                <DataTable.Row key={creditType}>
                  <DataTable.Cell>{fund}</DataTable.Cell>
                  <DataTable.Cell>{creditType}</DataTable.Cell>
                  <DataTable.Cell>
                    {getCreditTypeExpiration(creditType)}
                  </DataTable.Cell>
                  <DataTable.Cell numeric>
                    {props.lpCreditsByFund[fund][creditType]}
                  </DataTable.Cell>
                </DataTable.Row>
              );
            })}
            <DataTable.Row>
              <DataTable.Cell>{fund}</DataTable.Cell>
              <DataTable.Cell>&nbsp;</DataTable.Cell>
              <DataTable.Cell>Total:</DataTable.Cell>
              <DataTable.Cell numeric>{fundTotal}</DataTable.Cell>
            </DataTable.Row>
          </DataTable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  focusedSurface: {
    padding: 20,
    margin: 10,
    marginTop: 0,
    elevation: 5,
  },
  center: {
    width: '100%',
    alignSelf: 'center',
    textAlign: 'center',
  },
  content: {
    padding: 10,
  },
});
